// Single source of truth for the per-piece action matrix.
//
// Every consumer (NextActionBox on the piece detail page, Pipeline kanban
// card menus, Calendar piece popovers, Archive page rows) imports this
// hook so the action list stays consistent across surfaces. Returns a
// `primary` action that lives on the visible button and a `secondary[]`
// list tucked behind the dropdown chevron in a full-width button-dropdown.
//
// Action `kind` strings are stable identifiers. They are interpreted by
// the React consumers (which decide whether to fire immediately, open a
// dialog, or navigate to a URL) so adding new kinds doesn't require
// changing the action name on the backend.

export type PieceForActionMenu = {
  id: number
  publishing_status: string
  channel: string
  // Subset of piece.fields the action menu / dialogs need for guards
  // (e.g. YouTube visibility gating platform scheduling).
  fields?: Record<string, unknown>
  open_todos: { assignee: string; kind: string }[]
  slot: {
    manual_publish: boolean
    publishing_at: string | null
    publish_at: string
    upload_at?: string | null
    use_platform_scheduling?: boolean
  } | null
}

export type ChannelForActionMenu = {
  slug: string
  channel_type: string
  credentials_status: string
  // True when SparkDrop can't publish this channel automatically (no adapter,
  // e.g. YouTube Post / long-form, or the channel isn't connected). Drives
  // whether a pending piece is queued to the agent or the manual lane.
  publishes_manually?: boolean
  // Platform scheduling capability + defaults (added when the channel
  // type supports it — youtube_shorts, newsletter). Consumers gate the
  // ScheduleDialog's dual-time UI on these.
  allow_platform_scheduling?: boolean
  platform_scheduling_lead_hours?: number
  default_to_platform_scheduling?: boolean
  supports_platform_scheduling?: boolean
  forces_platform_scheduling?: boolean
}

export type ActionItem = {
  kind: ActionKind
  label: string
  // Optional destructive flag — used by the consumer to render the item
  // in danger styling and to require an explicit confirm step.
  destructive?: boolean
}

export type ActionKind =
  // Lifecycle transitions
  | "queue_for_scheduling"
  | "queue_for_manual" // park in the manual lane (queued_to_publish)
  | "unqueue"
  // Scheduling — opens the ScheduleDialog with optional pre-fill
  | "schedule"
  | "reschedule"
  // Backburner — copy varies by source state
  | "backburner"
  | "unschedule_and_backburner"
  | "dont_schedule_and_backburner"
  // Bring back from backburner / archive
  | "queue_for_scheduling_from_backburner"
  | "unarchive"
  // Manual-publish capture flows
  | "mark_published" // "Published?" (manual-publish slot, past slot time)
  | "already_published" // "Already published?" (any queued/scheduled/published piece)
  // Duplicate — copy a published piece into a fresh pending piece
  | "duplicate"
  // Archive / Delete
  | "archive"
  | "delete"

export type ActionMenu = {
  primary: ActionItem | null
  secondary: ActionItem[]
}

/**
 * Returns the {primary, secondary[]} action list for a given piece + its
 * channel.
 */
export function useActionMenu(
  piece: PieceForActionMenu,
  channel: ChannelForActionMenu | undefined,
): ActionMenu {
  const manualPublish = piece.slot?.manual_publish === true
  const slotPast = piece.slot ? new Date(piece.slot.publish_at).getTime() < Date.now() : false
  const channelPublishesManually = channel?.publishes_manually === true

  return baseMenu()

  function baseMenu(): ActionMenu {
  switch (piece.publishing_status) {
    case "pending":
      return list([
        channelPublishesManually
          ? item("queue_for_manual", "Queue for manual publishing")
          : item("queue_for_scheduling", "Queue for scheduling"),
        item("schedule", channelPublishesManually ? "Mark as scheduled…" : "Schedule…"),
        item("backburner", "Backburner"),
        item("delete", "Delete", { destructive: true }),
      ])

    // Manual lane — the operator schedules/publishes on the platform by hand.
    case "queued_to_publish":
      return list([
        item("schedule", "Mark as scheduled…"),
        item("mark_published", "Mark as published"),
        item("unqueue", "Move back to pending"),
        item("backburner", "Backburner"),
        item("delete", "Delete", { destructive: true }),
      ])

    case "queued_to_schedule":
      return list([
        item("schedule", "Schedule…"),
        item("already_published", "Already published?"),
        item("unqueue", "Move back to pending"),
        item("dont_schedule_and_backburner", "Don't schedule & backburner"),
        item("delete", "Delete", { destructive: true }),
      ])

    case "scheduled": {
      const base: ActionItem[] = []
      // Past-slot manual publishes get the "Published?" action up top.
      if (manualPublish && slotPast) {
        base.push(item("mark_published", "Published?"))
      }
      base.push(
        item("reschedule", "Reschedule"),
        item("already_published", "Already published?"),
        item("unschedule_and_backburner", "Unschedule & backburner"),
        item("delete", "Delete", { destructive: true }),
      )
      return list(base)
    }

    case "published":
      return list([
        item("already_published", "Update published URL…"),
        item("duplicate", "Duplicate as new piece"),
        item("archive", "Archive"),
        item("delete", "Delete", { destructive: true }),
      ])

    case "backburner":
      return list([
        item("queue_for_scheduling_from_backburner", "Queue for scheduling"),
        item("schedule", "Schedule…"),
        item("archive", "Archive"),
        item("delete", "Delete", { destructive: true }),
      ])

    case "archived":
      return list([
        item("unarchive", "Unarchive"),
        item("delete", "Delete", { destructive: true }),
      ])

    case "failed":
      return list([
        item("reschedule", "Reschedule"),
        item("backburner", "Backburner"),
        item("delete", "Delete", { destructive: true }),
      ])

    default:
      return { primary: null, secondary: [] }
  }
  }
}

function item(kind: ActionKind, label: string, opts: { destructive?: boolean } = {}): ActionItem {
  return { kind, label, destructive: opts.destructive }
}

function list(items: ActionItem[]): ActionMenu {
  if (items.length === 0) return { primary: null, secondary: [] }
  return { primary: items[0], secondary: items.slice(1) }
}
